import { useCallback, useEffect, useState } from 'react';
import type { ImageLoadEventData } from 'expo-image';

/**
 * Width/height ratio of a remote logo, learned from expo-image's onLoad and
 * cached per URL for the app's lifetime, so a mark that has loaded once
 * never re-measures (and never shifts its row again) on later mounts.
 * Insurer marks are wordmarks of unknown width; a fixed square box squashed
 * them. Until the first load the caller gets `fallback`, a typical wordmark
 * ratio, so the plate is roughly the right width from the first frame.
 */
const cache = new Map<string, number>();

export function useLogoAspect(url: string, fallback = 4) {
  const [aspect, setAspect] = useState(() => cache.get(url) ?? fallback);

  useEffect(() => {
    setAspect(cache.get(url) ?? fallback);
  }, [url, fallback]);

  const onLoad = useCallback(
    (e: ImageLoadEventData) => {
      const { width, height } = e.source;
      if (width > 0 && height > 0) {
        const next = width / height;
        cache.set(url, next);
        setAspect(next);
      }
    },
    [url]
  );

  return { aspect, onLoad };
}
